// Track-first helpers for the v4 Market Intelligence dashboard.
// Pure deterministic functions — no React, no network, no LLM.

const PRIMARY_ROLES = new Set(["CORE_TRACK", "WATCHLIST_TRACK"]);

const RAW_ARRAY_KEYS = [
    "RepresentativeCompaniesJSON",
    "RepresentativeCompanyTypesJSON",
    "RepresentativeBlockchainsJSON",
    "RepresentativeAssetClassesJSON",
    "KeyActorsJSON",
    "PrimaryAudienceJSON",
    "ObservedYearWeeksJSON",
    "ObservedWeeksJSON",
    "WeeklyFramingsJSON",
    "WeeklyHistoryJSON",
    "TypicalEvidenceJSON",
];

const PIPED_TO_ARRAY_KEYS = [
    ["RepresentativeCompanies", "RepresentativeCompaniesJSON"],
    ["RepresentativeCompanyTypes", "RepresentativeCompanyTypesJSON"],
    ["RepresentativeBlockchains", "RepresentativeBlockchainsJSON"],
    ["RepresentativeAssetClasses", "RepresentativeAssetClassesJSON"],
    ["KeyActors", "KeyActorsJSON"],
    ["PrimaryAudience", "PrimaryAudienceJSON"],
    ["ObservedYearWeeks", "ObservedYearWeeksJSON"],
    ["ObservedWeeks", "ObservedWeeksJSON"],
    ["WeeklyFramings", "WeeklyFramingsJSON"],
];

const splitPiped = (s) =>
    typeof s === "string"
        ? s.split("|").map((x) => x.trim()).filter(Boolean)
        : [];

/** v5 governed-signal-architecture roles. Read from `governance.dashboardInclusion`
 *  when present; falls through to legacy `CuratedDisplayRole`. */
export const GOVERNED_ROLES = {
    TOP_LEVEL: "MARKET_VIEW_TOP_LEVEL",
    SUB_PATTERN: "SUPPORTING_DETAIL_ONLY",
    WEEKLY_DYNAMIC: "WEEKLY_DYNAMIC_ONLY",
};

/** Defensive shaping of a raw v4 marketTrack row (no key renaming). */
export const normalizeTrack = (raw) => {
    if (!raw || typeof raw !== "object") return null;
    const out = { ...raw };
    for (const k of RAW_ARRAY_KEYS) {
        if (!Array.isArray(out[k])) out[k] = [];
    }
    for (const [pipedKey, jsonKey] of PIPED_TO_ARRAY_KEYS) {
        if ((!out[jsonKey] || !out[jsonKey].length) && typeof out[pipedKey] === "string") {
            out[jsonKey] = splitPiped(out[pipedKey]);
        }
    }
    out._role = (out.CuratedDisplayRole || "").toUpperCase();
    out._isPrimary = PRIMARY_ROLES.has(out._role);
    out._isSubPattern = out._role === "SUB_PATTERN";
    out._isUncurated = !out.CuratedDisplayRole;
    // v5 governance — feature-detected; absent on legacy JSONs. We keep
    // `governance` / `structureCounts` as-is and surface a lightweight
    // `_governed` summary so consumers don't need to know the upstream
    // field shape.
    const gov = out.governance && typeof out.governance === "object" ? out.governance : null;
    const inclusion = gov ? String(gov.dashboardInclusion || "").toUpperCase() : "";
    out._governed = gov
        ? {
              inclusion,
              isTopLevel: inclusion === GOVERNED_ROLES.TOP_LEVEL,
              isSubPattern: inclusion === GOVERNED_ROLES.SUB_PATTERN,
              isWeeklyDynamic: inclusion === GOVERNED_ROLES.WEEKLY_DYNAMIC,
              momentumLabel: typeof gov.momentumLabel === "string" ? gov.momentumLabel : null,
              evidenceQuality: typeof gov.evidenceQuality === "string" ? gov.evidenceQuality : null,
              evidenceQualityLabel: typeof gov.evidenceQualityLabel === "string" ? gov.evidenceQualityLabel : null,
              displayRoleLabel: typeof gov.displayRoleLabel === "string" ? gov.displayRoleLabel : null,
              dashboardInclusionLabel: typeof gov.dashboardInclusionLabel === "string" ? gov.dashboardInclusionLabel : null,
              marketZoneLabel: typeof gov.marketZoneLabel === "string" ? gov.marketZoneLabel : null,
              strategicUrgencyScore: typeof gov.strategicUrgencyScore === "number" ? gov.strategicUrgencyScore : null,
              signature: typeof gov.signature === "string" ? gov.signature : (typeof out.SignatureOneSentence === "string" ? out.SignatureOneSentence : null),
              marketZone: typeof gov.marketZone === "string" ? gov.marketZone : null,
              parentTrackId: typeof gov.parentTrackId === "string" ? gov.parentTrackId : (typeof gov.ParentTrackID === "string" ? gov.ParentTrackID : null),
              publishable: gov.publishable === true || gov.publishable === "YES" || gov.publishable === "TRUE",
          }
        : null;
    return out;
};

/** Defensive publish-safety gate. Returns `false` when the publisher's
 *  governance fields mark a track as non-publishable / review-only.
 *  The new publisher already excludes these from `marketTracks` and
 *  `marketTrackStructure`, but the frontend guard ensures we never
 *  surface them even if a legacy / partial payload leaks one through.
 *  Five disqualifying conditions are checked, mirroring the publisher
 *  contract verbatim. */
export const isPublishable = (track) => {
    if (!track) return false;
    const gov = track.governance && typeof track.governance === "object" ? track.governance : null;
    if (gov && gov.publishable === false) return false;
    if (track.GovernedRegistryPublishable && track.GovernedRegistryPublishable !== "YES") return false;
    if (track.GovernanceManualReviewRequired === "YES") return false;
    if (track.GovernedRegistryRowStatus === "REQUIRES_MANUAL_REVIEW") return false;
    if (track.GovernedDashboardInclusion === "REVIEW_QUEUE_ONLY") return false;
    return true;
};

export const normalizeTracks = (list) => {
    if (!Array.isArray(list)) return [];
    // Normalise first, then defensively drop any row the publisher
    // marked as non-publishable / review-only. The publisher should
    // already have excluded these from `marketTracks`; the guard is
    // belt-and-braces.
    return list.map(normalizeTrack).filter(Boolean).filter(isPublishable);
};

/** Default visible board.
 *
 *  Governed JSON (v5): only `MARKET_VIEW_TOP_LEVEL` tracks appear in the
 *  default table. The toggle widens to include
 *  `SUPPORTING_DETAIL_ONLY` (sub-patterns) too. Weekly dynamics never
 *  appear in the table by default — they live under their parent.
 *
 *  Legacy JSON (no `governance`): CORE + WATCHLIST. Sub-patterns and
 *  uncurated rows are hidden by default.
 *
 *  The function tolerates a mixed list (any track without `_governed`
 *  falls through to the legacy rule).
 */
export const filterBoardTracks = (tracks, { showExploratory = false } = {}) => {
    if (!Array.isArray(tracks)) return [];
    // Detect whether the upstream JSON ships governed metadata at all.
    const hasGoverned = tracks.some((t) => t && t._governed);
    if (hasGoverned) {
        if (showExploratory) {
            // Include all governed buckets — top-level + sub-patterns +
            // weekly movements. This is the "All governed market signals"
            // view (40 of 40 on the current dataset).
            return tracks.filter((t) => {
                if (!t) return false;
                if (t._governed) return true;
                // Mixed legacy fallback for any non-governed row
                return !t._isSubPattern;
            });
        }
        return tracks.filter((t) => {
            if (!t) return false;
            if (t._governed) return t._governed.isTopLevel;
            return t._isPrimary;
        });
    }
    // Legacy behaviour preserved verbatim
    if (showExploratory) {
        return tracks.filter((t) => !t._isSubPattern);
    }
    return tracks.filter((t) => t._isPrimary);
};

/** Sub-patterns belonging to a parent track. Governed JSONs use
 *  `governance.dashboardInclusion === "SUPPORTING_DETAIL_ONLY"` plus
 *  `ParentTrackID`; legacy JSONs fall back to `_isSubPattern` + parent id. */
export const subPatternsFor = (track, allTracks) => {
    if (!track || !Array.isArray(allTracks)) return [];
    const parentId = track.TrackID;
    const canonical = track.CanonicalTrackID;
    return allTracks.filter((t) => {
        if (!t || t.TrackID === parentId) return false;
        const governedSubPattern = t._governed && t._governed.isSubPattern;
        const legacySubPattern = t._isSubPattern;
        if (!governedSubPattern && !legacySubPattern) return false;
        if (t.ParentTrackID && t.ParentTrackID === parentId) return true;
        if (canonical && t.CanonicalTrackID === canonical && t.TrackID !== parentId) return true;
        return false;
    });
};

/** Weekly-dynamic tracks tied to a parent (governed JSON only). Falls back
 *  to an empty list on legacy JSONs. */
export const weeklyDynamicsFor = (track, allTracks) => {
    if (!track || !Array.isArray(allTracks)) return [];
    const parentId = track.TrackID;
    const canonical = track.CanonicalTrackID;
    return allTracks.filter((t) => {
        if (!t || t.TrackID === parentId) return false;
        if (!t._governed || !t._governed.isWeeklyDynamic) return false;
        if (t.ParentTrackID && t.ParentTrackID === parentId) return true;
        if (canonical && t.CanonicalTrackID === canonical) return true;
        return false;
    });
};

/** Related top-level tracks: same MacroTheme or PrimaryUseCase (excluding
 *  the track itself and its sub-patterns). */
export const relatedTracksFor = (track, allTracks, { limit = 4 } = {}) => {
    if (!track || !Array.isArray(allTracks)) return [];
    const out = [];
    for (const t of allTracks) {
        if (!t || t.TrackID === track.TrackID) continue;
        if (!t._isPrimary) continue;
        let reason = null;
        if (t.MacroTheme && t.MacroTheme === track.MacroTheme) reason = "Same market zone";
        else if (t.PrimaryUseCase && t.PrimaryUseCase === track.PrimaryUseCase) reason = "Adjacent use case";
        else if (t.RegulatorySplit && t.RegulatorySplit === track.RegulatorySplit && t.PrimaryMechanism === track.PrimaryMechanism) reason = "Related infrastructure layer";
        if (reason) out.push({ track: t, reason });
    }
    return out.slice(0, limit);
};

const ROLE_RANK = { CORE_TRACK: 0, WATCHLIST_TRACK: 1, SUB_PATTERN: 2, EXPLORATORY_CLUSTER: 3 };

/** Default sort: CORE > WATCHLIST > active > persistence > avg score > total sources. */
export const sortTracksDefault = (list) => {
    if (!Array.isArray(list)) return [];
    return [...list].sort((a, b) => {
        const ra = ROLE_RANK[a._role] ?? 99;
        const rb = ROLE_RANK[b._role] ?? 99;
        if (ra !== rb) return ra - rb;
        const aActive = (a.IsActiveInLatestWeek || "").toUpperCase() === "YES" ? 0 : 1;
        const bActive = (b.IsActiveInLatestWeek || "").toUpperCase() === "YES" ? 0 : 1;
        if (aActive !== bActive) return aActive - bActive;
        const ap = a.PersistenceRatio ?? 0;
        const bp = b.PersistenceRatio ?? 0;
        if (bp !== ap) return bp - ap;
        const ag = a.AvgScore ?? a.LatestScore ?? 0;
        const bg = b.AvgScore ?? b.LatestScore ?? 0;
        if (bg !== ag) return bg - ag;
        return (b.TotalSources ?? 0) - (a.TotalSources ?? 0);
    });
};

/** Sort timeline rows ascending by WeekIndex. */
export const sortHistoryAsc = (rows) => {
    if (!Array.isArray(rows)) return [];
    return [...rows].sort((a, b) => (a.WeekIndex || 0) - (b.WeekIndex || 0));
};

/** Extract a readable domain from a URL. Returns null on failure. */
export const domainFromUrl = (url) => {
    if (!url || typeof url !== "string") return null;
    try {
        const u = new URL(url);
        return (u.hostname || "").replace(/^www\./, "") || null;
    } catch {
        // Fallback for malformed URLs: take the substring between // and first /
        const m = url.match(/^[a-zA-Z]+:\/\/([^/?#]+)/);
        return m ? m[1].replace(/^www\./, "") : null;
    }
};

/** Pretty-publisher rules for the most common business-news domains. */
const PUBLISHER_OVERRIDES = {
    "businessinsider.com": "Business Insider",
    "markets.businessinsider.com": "Markets · Business Insider",
    "msn.com": "MSN",
    "prnewswire.com": "PR Newswire",
    "globenewswire.com": "GlobeNewswire",
    "theglobeandmail.com": "The Globe and Mail",
    "eqs-news.com": "EQS News",
    "dailycoin.com": "DailyCoin",
    "crypto-reporter.com": "Crypto Reporter",
    "coindesk.com": "CoinDesk",
    "cointelegraph.com": "Cointelegraph",
    "theblock.co": "The Block",
    "bloomberg.com": "Bloomberg",
    "reuters.com": "Reuters",
    "ft.com": "Financial Times",
    "wsj.com": "Wall Street Journal",
};

/** Best-effort human-readable publisher label from an explicit publisher
 *  field or a URL domain. */
export const publisherLabelFor = (sourceLike) => {
    if (!sourceLike) return null;
    const explicit = sourceLike.publisher || sourceLike.source || sourceLike.Source;
    if (explicit && typeof explicit === "string") return explicit;
    const dom = domainFromUrl(sourceLike.url || sourceLike.URL || sourceLike.link);
    if (!dom) return null;
    return PUBLISHER_OVERRIDES[dom] || dom;
};

/** Top-N sources from a TopSourcesJSON entry (handles list of dicts or pipe strings). */
export const parseTopSources = (raw, limit = 5) => {
    if (!raw) return [];
    if (Array.isArray(raw)) {
        return raw.slice(0, limit).map((s) => {
            if (typeof s === "string") return { url: s, publisher: publisherLabelFor({ url: s }) };
            const url = s.url || s.URL || s.link || null;
            const explicitTitle = s.title || s.headline || null;
            const title = (typeof explicitTitle === "string" && explicitTitle.trim()) ? explicitTitle.trim() : null;
            return {
                url,
                title,
                publisher: publisherLabelFor(s) || (url ? publisherLabelFor({ url }) : null),
                publishedAt: s.publishedAt || s.date || s.PublishedAt || s.Date || null,
                relevanceScore: typeof s.relevanceScore === "number" ? s.relevanceScore : (typeof s.RelevanceScore === "number" ? s.RelevanceScore : null),
                companyCount: typeof s.companyCount === "number" ? s.companyCount : null,
                assetClassCount: typeof s.assetClassCount === "number" ? s.assetClassCount : null,
                blockchainCount: typeof s.blockchainCount === "number" ? s.blockchainCount : null,
            };
        });
    }
    if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw);
            return parseTopSources(parsed, limit);
        } catch {
            return raw.split("|").slice(0, limit).map((u) => ({ url: u.trim(), publisher: publisherLabelFor({ url: u.trim() }) })).filter((s) => s.url);
        }
    }
    return [];
};

/** Curation-summary fallback when the upstream wrapper omits it. */
export const deriveCurationSummary = (tracks) => {
    const out = { total: 0, coreTracks: 0, watchlistTracks: 0, subPatterns: 0, exploratoryClusters: 0, uncurated: 0, active: 0, dormant: 0, stale: 0 };
    if (!Array.isArray(tracks)) return out;
    for (const t of tracks) {
        out.total += 1;
        const role = (t.CuratedDisplayRole || "").toUpperCase();
        if (role === "CORE_TRACK") out.coreTracks += 1;
        else if (role === "WATCHLIST_TRACK") out.watchlistTracks += 1;
        else if (role === "SUB_PATTERN") out.subPatterns += 1;
        else if (role === "EXPLORATORY_CLUSTER") out.exploratoryClusters += 1;
        else out.uncurated += 1;
        const lc = (t.LifecycleStatus || "").toUpperCase();
        if (lc === "ACTIVE") out.active += 1;
        else if (lc === "DORMANT") out.dormant += 1;
        else if (lc === "STALE") out.stale += 1;
    }
    return out;
};

export const roleLabel = (role) => {
    switch ((role || "").toUpperCase()) {
        case "CORE_TRACK": return "Core signal";
        case "WATCHLIST_TRACK": return "Watchlist signal";
        case "SUB_PATTERN": return "Sub-pattern";
        case "EXPLORATORY_CLUSTER": return "Exploratory cluster";
        default: return "Uncurated";
    }
};

export const roleTone = (role) => {
    switch ((role || "").toUpperCase()) {
        case "CORE_TRACK": return "ink";
        case "WATCHLIST_TRACK": return "warning";
        case "SUB_PATTERN": return "muted";
        default: return "muted";
    }
};

export const visibilityTone = (vis) => {
    const v = (vis || "").toUpperCase();
    if (v === "SIGNAL") return "ink";
    if (v === "WATCHLIST") return "warning";
    return "muted";
};

export const lifecycleTone = (lc) => {
    const v = (lc || "").toUpperCase();
    if (v === "ACTIVE") return "positive";
    if (v === "DORMANT") return "muted";
    if (v === "STALE") return "negative";
    return "muted";
};


// ---------------------------------------------------------------------------
// Market structure lenses — group visible tracks by a categorical field
// (PrimaryUseCase / PrimaryMechanism), segmented by curated role.
// ---------------------------------------------------------------------------

const SEGMENT_ORDER = ["core", "watchlist", "subPattern", "earlyStage"];

const segmentOf = (track) => {
    const role = (track && track._role) || (track && (track.CuratedDisplayRole || "").toUpperCase()) || "";
    if (role === "CORE_TRACK") return "core";
    if (role === "WATCHLIST_TRACK") return "watchlist";
    if (role === "SUB_PATTERN") return "subPattern";
    return "earlyStage";
};

/** Group `tracks` by a string field. Returns:
 *  [{ key, total, segments: { core, watchlist, subPattern, earlyStage } }]
 *  sorted by total desc, then key asc. Tracks with empty key are dropped. */
export const groupTracksBy = (tracks, field) => {
    if (!Array.isArray(tracks) || !field) return [];
    const m = new Map();
    for (const t of tracks) {
        const key = (t && t[field]) || null;
        if (!key) continue;
        if (!m.has(key)) {
            m.set(key, { key, total: 0, segments: { core: 0, watchlist: 0, subPattern: 0, earlyStage: 0 } });
        }
        const row = m.get(key);
        row.total += 1;
        row.segments[segmentOf(t)] += 1;
    }
    return [...m.values()]
        .sort((a, b) => (b.total - a.total) || a.key.localeCompare(b.key));
};

export const SEGMENT_LABELS = {
    core: "Core signal",
    watchlist: "Watchlist",
    subPattern: "Sub-pattern",
    earlyStage: "Early-stage",
};

export const segmentTone = (seg) => {
    switch (seg) {
        case "core": return "ink";
        case "watchlist": return "warning";
        case "subPattern": return "muted";
        default: return "mute";
    }
};

export { SEGMENT_ORDER };

// ---------------------------------------------------------------------------
// Comparative score series — Selected vs Core average vs Watchlist average,
// computed weekly across the union of available indexed weeks.
// ---------------------------------------------------------------------------

const avgOrNull = (xs) => {
    const valid = xs.filter((v) => typeof v === "number" && !Number.isNaN(v));
    if (!valid.length) return null;
    const sum = valid.reduce((a, b) => a + b, 0);
    return Math.round((sum / valid.length) * 10) / 10;
};

/** Build the score-by-week comparison series.
 *
 *  - selected: { trackId, label, points: [{x: weekIndex, y, label: yearWeek}] }
 *  - coreAvg / watchlistAvg: { label, points: [{x, y, label}] }
 *  - allWeeks: sorted unique [{ weekIndex, yearWeek }]
 *
 *  Tracks without a CuratedDisplayRole are ignored for the averages.
 *  Returns `null` if there is no usable history.
 */
export const buildComparisonSeries = ({ tracks, historyByTrackId, selectedTrack }) => {
    if (!Array.isArray(tracks) || !historyByTrackId) return null;

    // Collect weeks across all available histories so the x-axis is shared.
    const weekMap = new Map(); // weekIndex -> yearWeek
    for (const list of Object.values(historyByTrackId)) {
        for (const h of list || []) {
            if (typeof h.WeekIndex !== "number") continue;
            if (!weekMap.has(h.WeekIndex)) weekMap.set(h.WeekIndex, h.YearWeek || String(h.WeekIndex));
        }
    }
    if (!weekMap.size) return null;
    const allWeeks = [...weekMap.entries()]
        .map(([weekIndex, yearWeek]) => ({ weekIndex, yearWeek }))
        .sort((a, b) => a.weekIndex - b.weekIndex);

    const scoreFor = (trackId, weekIndex) => {
        const list = historyByTrackId[trackId] || [];
        const row = list.find((h) => h.WeekIndex === weekIndex);
        return row && typeof row.Score === "number" ? row.Score : null;
    };

    const coreIds = tracks.filter((t) => t._role === "CORE_TRACK").map((t) => t.TrackID);
    const watchIds = tracks.filter((t) => t._role === "WATCHLIST_TRACK").map((t) => t.TrackID);

    const buildAvg = (ids, label) => ({
        label,
        points: allWeeks.map(({ weekIndex, yearWeek }) => ({
            x: weekIndex,
            y: avgOrNull(ids.map((id) => scoreFor(id, weekIndex))),
            label: yearWeek,
        })),
    });

    const selected = selectedTrack && selectedTrack.TrackID
        ? {
            trackId: selectedTrack.TrackID,
            label: selectedTrack.StableTrackName || selectedTrack.SuggestedDisplayLabel || "Selected signal",
            points: allWeeks.map(({ weekIndex, yearWeek }) => ({
                x: weekIndex,
                y: scoreFor(selectedTrack.TrackID, weekIndex),
                label: yearWeek,
            })),
        }
        : null;

    return {
        allWeeks,
        selected,
        coreAvg: coreIds.length ? buildAvg(coreIds, "Core signal average") : null,
        watchlistAvg: watchIds.length ? buildAvg(watchIds, "Watchlist average") : null,
    };
};

// ---------------------------------------------------------------------------
// Market signal maturity interpretation — short, deterministic, executive-friendly.
// ---------------------------------------------------------------------------

const joinList = (items) => {
    const arr = items.filter(Boolean);
    if (!arr.length) return "";
    if (arr.length === 1) return arr[0];
    if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
    return `${arr.slice(0, -1).join(", ")}, and ${arr[arr.length - 1]}`;
};

/** Returns 1–3 short paragraphs describing the maturity distribution.
 *  No LLM. Driven by the curation summary + macro themes of core / watchlist. */
export const buildMaturityInterpretation = ({ summary, tracks }) => {
    const out = [];
    if (!summary) return out;

    const core = summary.coreTracks || 0;
    const watch = summary.watchlistTracks || 0;
    const subs = summary.subPatterns || 0;
    const early = (summary.exploratoryClusters || 0) + (summary.uncurated || 0);

    const coreThemes = [];
    const watchThemes = [];
    if (Array.isArray(tracks)) {
        const seenC = new Set();
        const seenW = new Set();
        for (const t of tracks) {
            const theme = (t.MacroTheme || "").trim();
            if (!theme) continue;
            if (t._role === "CORE_TRACK" && !seenC.has(theme)) {
                seenC.add(theme);
                coreThemes.push(theme);
            } else if (t._role === "WATCHLIST_TRACK" && !seenW.has(theme)) {
                seenW.add(theme);
                watchThemes.push(theme);
            }
        }
    }

    if (core > 0) {
        const themePart = coreThemes.length ? ` in ${joinList(coreThemes.slice(0, 4).map((t) => t.toLowerCase()))}` : "";
        out.push(`${core === 1 ? "One core signal currently defines" : `${core} core signals currently define`} the strongest market structure${themePart}.`);
    }
    if (watch > 0) {
        const themePart = watchThemes.length ? ` around ${joinList(watchThemes.slice(0, 4).map((t) => t.toLowerCase()))}` : "";
        out.push(`Watchlist signals point to where evidence is forming but is not yet fully confirmed${themePart}.`);
    }
    if (subs > 0) {
        out.push(`${subs} sub-pattern${subs === 1 ? "" : "s"} sit beneath the main signals as adjacent evidence clusters.`);
    }
    // Detect governed dataset so we can swap "Early-stage signals…"
    // wording for the governed "supporting signals" framing.
    const hasGoverned = Array.isArray(tracks) && tracks.some((t) => t && t._governed);
    let governedSupporting = 0;
    if (hasGoverned) {
        for (const t of tracks) {
            if (!t || !t._governed) continue;
            if (t._governed.isSubPattern || t._governed.isWeeklyDynamic) governedSupporting += 1;
        }
    }
    if (hasGoverned) {
        if (governedSupporting >= 5) {
            out.push("Supporting signals remain inspectable, but are kept off the default board.");
        }
    } else if (early >= 5) {
        out.push("Early-stage signals remain visible but are kept off the default board to avoid over-reading weak evidence.");
    }
    return out;
};

// ---------------------------------------------------------------------------
// v5 governed-signal-architecture display helpers (all feature-detected).
// ---------------------------------------------------------------------------

/** Primary role chip for a track. Honours v5 `governance.dashboardInclusion`
 *  first, then falls through to legacy `CuratedDisplayRole`. Returns a
 *  single, deduplicated label — never both "Watchlist signal" + "Watchlist
 *  Track". When neither source classifies the track, returns `null` so the
 *  caller can render a fallback or omit the chip entirely. */
export const primaryRoleChipForTrack = (track) => {
    if (!track) return null;
    const g = track._governed;
    if (g) {
        // Prefer the publisher's pre-humanised label when present (new
        // safety-layer field). Tone is still inferred from _role so
        // watchlist stays orange, core stays ink, etc.
        if (typeof g.displayRoleLabel === "string" && g.displayRoleLabel.trim()) {
            let tone = "muted";
            if (g.isTopLevel) tone = track._role === "WATCHLIST_TRACK" ? "warning" : "ink";
            else if (g.isWeeklyDynamic) tone = "accent";
            return { label: g.displayRoleLabel.trim(), tone };
        }
        if (g.isTopLevel) {
            // CORE_TRACK vs WATCHLIST_TRACK still distinguishes top-level by
            // conviction; sub-pattern + weekly-dynamic are handled below.
            if (track._role === "CORE_TRACK") return { label: "Core signal", tone: "ink" };
            if (track._role === "WATCHLIST_TRACK") return { label: "Watchlist signal", tone: "warning" };
            return { label: "Top-level signal", tone: "ink" };
        }
        if (g.isSubPattern) return { label: "Sub-pattern", tone: "muted" };
        if (g.isWeeklyDynamic) return { label: "Weekly movement", tone: "accent" };
    }
    if (track._role === "CORE_TRACK") return { label: "Core signal", tone: "ink" };
    if (track._role === "WATCHLIST_TRACK") return { label: "Watchlist signal", tone: "warning" };
    if (track._role === "SUB_PATTERN") return { label: "Sub-pattern", tone: "muted" };
    if (track._role === "EXPLORATORY_CLUSTER") return { label: "Exploratory", tone: "muted" };
    return null;
};

/** Reader-friendly momentum label.
 *  Source priority:
 *    1. `track.governance.momentumLabel`  (governed; already a label)
 *    2. `track.LatestMomentum`            (legacy enum like `STABLE`)
 *    3. derived from `ScoreTrend4w`       (numeric delta heuristic)
 *  Returns `null` when nothing is available. Never returns a raw enum. */
const MOMENTUM_ENUM_LABEL = {
    ACCELERATING: "Gaining momentum",
    STRENGTHENING: "Gaining momentum",
    GAINING: "Gaining momentum",
    STABLE: "Stable",
    MIXED: "Mixed momentum",
    FADING: "Fading",
    SOFTENING: "Fading",
    WEAKENING: "Fading",
    NEW: "New weekly movement",
    NEW_WEEKLY_MOVEMENT: "New weekly movement",
    NO_CURRENT_DYNAMIC: "No current weekly movement",
    NO_CURRENT_DYNAMICS: "No current weekly movement",
};
export const momentumLabelForTrack = (track) => {
    if (!track) return null;
    const fromGov = track._governed && track._governed.momentumLabel;
    if (typeof fromGov === "string" && fromGov.trim()) {
        // Governed source is already a display string but may still be a
        // capitalised enum — map through the same lookup just in case.
        const u = fromGov.trim().toUpperCase();
        return MOMENTUM_ENUM_LABEL[u] || fromGov.trim();
    }
    const legacy = track.LatestMomentum;
    if (typeof legacy === "string" && legacy.trim()) {
        const u = legacy.trim().toUpperCase();
        if (MOMENTUM_ENUM_LABEL[u]) return MOMENTUM_ENUM_LABEL[u];
    }
    const trend = typeof track.ScoreTrend4w === "number" ? track.ScoreTrend4w : null;
    if (trend == null) return null;
    if (trend >= 6) return "Gaining momentum";
    if (trend <= -6) return "Fading";
    if (trend >= 2) return "Mixed momentum";
    if (trend <= -2) return "Mixed momentum";
    return "Stable";
};

/** Reader-friendly evidence quality label (governed JSON only — `null`
 *  otherwise). */
const EVIDENCE_QUALITY_LABEL = {
    BROAD_MULTI_ACTOR: "Broad evidence base",
    NARROW_CONCENTRATED: "Narrow evidence base",
    SINGLE_POINT: "Single-week evidence",
    LOW_EVIDENCE: "Limited evidence",
};
export const evidenceQualityLabelForTrack = (track) => {
    // Prefer the publisher's pre-humanised label when present (new
    // safety-layer field), then fall back to mapping the raw enum.
    const labelFromGov = track && track._governed && track._governed.evidenceQualityLabel;
    if (typeof labelFromGov === "string" && labelFromGov.trim()) return labelFromGov.trim();
    const raw = track && track._governed && track._governed.evidenceQuality;
    if (typeof raw !== "string" || !raw.trim()) return null;
    const u = raw.trim().toUpperCase();
    return EVIDENCE_QUALITY_LABEL[u] || null;
};

/** Strategic urgency chip — only renders for a positive score. Returns
 *  `null` for `0`, missing, or invalid scores so the UI never shows
 *  "Low urgency" for tracks with no current-week activity. */
export const strategicUrgencyChipForTrack = (track) => {
    const score = track && track._governed && track._governed.strategicUrgencyScore;
    if (typeof score !== "number" || !Number.isFinite(score) || score <= 0) return null;
    if (score >= 70) return { label: "High strategic urgency", tone: "negative" };
    if (score >= 40) return { label: "Medium strategic urgency", tone: "warning" };
    return { label: "Low strategic urgency", tone: "muted" };
};

/** "Market zone" — the user-facing label that replaces `MacroTheme`.
 *  Governed JSON ships an explicit `governance.marketZone`; legacy JSON
 *  falls back to `MacroTheme` (the previous label). */
export const marketZoneForTrack = (track) => {
    if (!track) return null;
    const g = track._governed;
    // Prefer the publisher's pre-humanised label (new safety-layer field),
    // then the raw governed market zone, then legacy MacroTheme.
    if (g && typeof g.marketZoneLabel === "string" && g.marketZoneLabel.trim()) return g.marketZoneLabel.trim();
    if (g && typeof g.marketZone === "string" && g.marketZone.trim()) return g.marketZone.trim();
    if (typeof track.MacroTheme === "string" && track.MacroTheme.trim()) return track.MacroTheme.trim();
    return null;
};

/** Concise "Signal definition" sentence (governed JSON only). Falls back
 *  to `null` on legacy JSONs so the caller can omit the section entirely. */
export const signalDefinitionForTrack = (track) => {
    if (!track) return null;
    const g = track._governed;
    if (g && typeof g.signature === "string" && g.signature.trim()) return g.signature.trim();
    if (typeof track.SignatureOneSentence === "string" && track.SignatureOneSentence.trim()) return track.SignatureOneSentence.trim();
    return null;
};
